import cn from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { type AnchorHTMLAttributes, type CSSProperties, forwardRef } from "react";

import { handleIframeNavigation } from "../utils/iframe";
import { AccessibleLoadingState } from "./AccessibleLoadingState";
import { type ButtonProps } from "./ButtonProps";
import { hydrateVariant } from "./hydrateVariant";

export interface LinkButtonProps
	// We use `color` for overall button color.
	extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "color">,
		ButtonProps {
	href: string;
	disabled?: boolean;
}

// This generally is bad practice, but this is only used inside an iframe where there is no
// Next.js router. This means this *shouldn't* change during runtime and won't cause React to complain.
const useMaybeRouter = (): ReturnType<typeof useRouter> | undefined => {
	try {
		return useRouter();
	} catch {
		// useRouter is not available outside of Next.js context
		return undefined;
	}
};

/**
 * The standard implementation of a link styled to look like a button, allowing users navigate to
 * other pages with standard mouse, touch, and keyboard inputs.
 *
 * `preset` is the only property you need to set in most cases. It provides sensible defaults for
 * all the properties exposed by this API, *e.g.*, the button `color` and `variant`. When these
 * defaults are not sufficient, you can override any value by providing the relevant properties
 * manually, *e.g.*, `color="negative"` or `variant="outlined"`.
 *
 * Use the `startIcon` and `endIcon` props, rather than providing icons as children. This will
 * provide a more consistent and accessible experience to a wide variety of devices and users,
 * including screenreaders.
 */
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
	(
		{
			className,
			preset,
			variant,
			size,
			color,
			borderRadius,
			elevation,
			startIcon,
			endIcon,
			children,
			toggled,
			loading,
			...props
		},
		ref
	) => {
		const router = useMaybeRouter();

		const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
			handleIframeNavigation(e, props.href, router);
		};

		return (
			<Link
				{...props}
				onClick={onClick}
				ref={ref}
				data-toggled={toggled === true}
				data-loading={loading === true}
				className={cn(
					"relative",
					"cursor-pointer",
					"no-underline",
					"hover:underline",
					"focus:underline",
					"active:underline",
					"[&[data-pressed='true']]:underline",
					hydrateVariant({ preset, variant, size, color, borderRadius, elevation }),
					className
				)}
				// HACK: If we do not set this, links on dark mode (only links and only dark mode)
				// have a focus offset ring is colored #fff. Looking at the CSS inspector the
				// classes of the form `ring-offset-*` (e.g., `ring-offset-transparent`) does not
				// look like it exists at all. I'm not sure what's going on. Anyway, this fixes it!
				style={{ "--tw-ring-offset-color": "var(--bg-primary)" } as CSSProperties}
			>
				<AccessibleLoadingState loading={loading} startIcon={startIcon} endIcon={endIcon}>
					{children}
				</AccessibleLoadingState>
			</Link>
		);
	}
);
LinkButton.displayName = "LinkButton";
